import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { QrCode } from "lucide-react";
import { CatalogModal } from "./CatalogModal";
import { useContent, rich } from "@/lib/content";
import { Reveal, SectionTitle } from "./Primitives";
import { lenisRef } from "@/lib/smooth";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const Spotlight = () => {
  const { CAKES, TEXTS: T } = useContent();
  const [qr, setQr] = useState(false);
  const TABS = Object.keys(CAKES).filter((k) => k !== "Lo más vendido");
  const [tab, setTab] = useState(TABS[0]);
  const [active, setActive] = useState(0);
  const spot = useRef(null);
  const lock = useRef(0);
  // En celular la foto queda arriba de la lista: al tocar un pastel la traemos a la vista
  const pick = (k) => {
    setActive(k);
    const el = spot.current;
    if (!el || window.innerWidth >= 1024 || el.getBoundingClientRect().top >= 64) return;
    lock.current = Date.now() + 1200;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -80, duration: 0.8 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const list = CAKES[tab] ?? CAKES[TABS[0]] ?? [];
  const cur = list[active] ?? list[0] ?? { name: "", text: "" };

  return (
    <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-5">
        <div ref={spot} data-testid="cake-spotlight" className="relative mx-auto aspect-[4/3] w-full max-w-xl overflow-hidden rounded-[2rem] border border-gold/25 bg-roast lg:sticky lg:top-28 lg:aspect-[4/5] lg:max-w-none">
          {list.map((c, k) => (
            <motion.img
              key={`${tab}-${c.name}`}
              src={c.img}
              alt={c.name}
              initial={false}
              animate={{ opacity: k === active ? 1 : 0, scale: k === active ? 1 : 1.08 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ))}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,transparent_35%,rgba(20,13,9,0.85)_100%)]" />
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
            <p className="font-sans text-xs uppercase tracking-[0.3em] text-gold">{tab}</p>
            <h3 data-testid="cake-spotlight-name" className="mt-1 font-display text-2xl text-cream md:mt-2 md:text-4xl">{cur.name}</h3>
            <p className="mt-1 font-sans text-[13px] leading-relaxed text-sand md:mt-2 md:text-sm">{cur.text}</p>
          </div>
        </div>
      </div>
      <div className="lg:col-span-7">
        <div className="mb-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`cake-tab-${slug(t)}`}
              onClick={() => {
                setTab(t);
                setActive(0);
              }}
              className={`rounded-full border px-5 py-2.5 font-sans text-sm transition-colors ${
                t === tab ? "border-gold bg-gold text-espresso" : "border-gold/30 text-sand hover:border-gold hover:text-cream"
              }`}
            >
              {t}
            </button>
          ))}
          <button
            data-testid="cake-catalog-btn"
            onClick={() => setQr(true)}
            className="inline-flex items-center gap-2 rounded-full border border-gold px-5 py-2.5 font-sans text-sm text-gold transition-colors hover:bg-gold hover:text-espresso"
          >
            <QrCode size={16} /> {T.btn_pas_catalog}
          </button>
        </div>
        {qr && <CatalogModal src={T.pas_catalog_img} title={T.btn_pas_catalog} onClose={() => setQr(false)} />}
        <ul className={`grid grid-cols-2 gap-x-4 lg:gap-x-10 ${list.length > 7 ? "" : "lg:block"}`}>
          {list.map((c, k) => (
            <li key={`${tab}-${c.name}`}>
              <button
                data-testid={`cake-item-${slug(c.name)}`}
                onMouseEnter={() => Date.now() > lock.current && setActive(k)}
                onClick={() => pick(k)}
                className="group flex w-full items-baseline gap-2 border-b border-gold/15 py-3 text-left md:gap-5 lg:py-5"
              >
                <span className={`font-sans text-xs transition-colors ${k === active ? "text-gold" : "text-sand/70"}`}>{String(k + 1).padStart(2, "0")}</span>
                <span className={`font-display text-xl transition-[color,transform] duration-500 md:text-3xl ${list.length > 7 ? "" : "lg:text-4xl"} ${k === active ? "translate-x-2 text-gold" : "text-cream group-hover:translate-x-2"}`}>
                  {c.name}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const Pasteleria = () => {
  const { TEXTS: T } = useContent();
  return (
  <section id="pasteleria" data-testid="pasteleria-section" className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28 lg:py-40">
    <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <SectionTitle eyebrow={T.pas_eyebrow} title={rich(T.pas_title)} de={T.pas_de} testid="pasteleria-title" />
      <Reveal>
        <p className="max-w-sm font-sans text-sand">{T.pas_sub}</p>
      </Reveal>
    </div>
    <Spotlight />
  </section>
  );
};
